import useLoginCall from 'api/legacy/auth/useLoginCall';
import useLoginTotpCall from 'api/legacy/auth/useLoginTotpCall';
import useLogoutCall from 'api/legacy/auth/useLogoutCall';
import useUserOrganizations from 'api/legacy/organization/useUserOrganizations';
import useUserDetails from 'api/legacy/users/useUserDetails';
import { useCallback, useMemo, useState, type FC, type ReactNode } from 'react';
import { SWRConfig, type SWRConfiguration } from 'swr';
import type { OnLoggedIn } from 'types/legacyDashboard';
import { useDebounceCallback } from 'utils/hooks';
import AuthContext, { AuthContextProps } from './AuthContext';

interface AuthProviderProps {
  children?: ReactNode;
}

const authProviderSwrConfig: SWRConfiguration = {
  dedupingInterval: 60000,
  focusThrottleInterval: 3600000, // Only check for new info every hour (or at refresh)
  loadingTimeout: 3000,
  errorRetryInterval: 10000,
  errorRetryCount: 3,
};

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [loginResult, setLoginResult] = useState<OnLoggedIn>();
  const [loggingOut, setLoggingOut] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  const [loginError, setLoginError] = useState<string | undefined>(undefined);
  const [loginTotpError, setLoginTotpError] = useState<string | undefined>(undefined);
  const [logoutError, setLogoutError] = useState<string | undefined>(undefined);

  const loginApi = useLoginCall();
  const loginTotpApi = useLoginTotpCall();
  const logoutApi = useLogoutCall();

  const showMfaModal = !!loginResult?.mfa && !isAuthenticated;

  /* ---====== User details =====--- */

  const {
    me,
    error: userDetailsError,
    isLoading: loadingUserDetails,
    isValidating: validatingUserDetails,
    mutate,
  } = useUserDetails({
    canLoad: isAuthenticated || !(loggingIn || loggingOut || showMfaModal),
    config: {
      dedupingInterval: 60000,
      focusThrottleInterval: 60000,
      errorRetryInterval: 5000,
      errorRetryCount: 0,
    },
  });

  const { organizations, isLoading: loadingOrganizations } = useUserOrganizations(me.id);

  /* ---====== Check logged in state =====--- */
  const checkLoggedInStatus = useCallback(async () => {
    if (loggingIn || loggingOut || showMfaModal || validatingUserDetails || loadingUserDetails) {
      return;
    }

    if ((isAuthenticated && userDetailsError?.status === 403) || userDetailsError?.status === 401) {
      setIsAuthenticated(false);
    } else if (!isAuthenticated && !userDetailsError && !!me?.id) {
      setIsAuthenticated(true);
    }
  }, [
    isAuthenticated,
    loadingUserDetails,
    loggingIn,
    loggingOut,
    me,
    showMfaModal,
    userDetailsError,
    validatingUserDetails,
  ]);
  useDebounceCallback(checkLoggedInStatus, 250);

  /* ---====== Actions =====--- */

  const login = useCallback(
    async (email: string, password: string, rememberMe = false) => {
      setLoggingIn(true);
      setLoginError(undefined);

      let loginAttemptResult;
      try {
        loginAttemptResult = await loginApi(email, password);
        if (loginAttemptResult?.success) {
          setLoginResult({ ...loginAttemptResult?.data, email, rememberMe });
        } else {
          setLoginError('Invalid login attempt');
        }
      } catch (error) {
        setLoginError('Invalid login attempt');
      }

      if (!loginAttemptResult?.data.mfa && loginAttemptResult?.success) {
        setIsAuthenticated(true);
      }
      setLoggingIn(false);
    },
    [loginApi]
  );

  const loginTotp = useCallback(
    async (totpCode: string) => {
      if (!loginResult) {
        setLoginTotpError('Stale login attempt');
        return;
      }

      const { email, email_id: methodId, rememberMe } = loginResult;

      if (!methodId || methodId.length === 0 || !email || email.length === 0) {
        setLoginTotpError('Stale login attempt');
        return;
      }

      let successfulLogin = false;
      setLoginTotpError(undefined);
      setLoggingIn(true);
      try {
        const response = await loginTotpApi(email, methodId, totpCode, rememberMe ?? false);

        if (response?.success) {
          setLoginResult(response?.data);
          successfulLogin = true;
        } else if (response?.error) {
          if (typeof response.error === 'string') {
            setLoginTotpError(response.error);
          } else if (response.error.totpCode) {
            setLoginTotpError(response.error.totpCode);
          }
        } else {
          setLoginTotpError('Invalid login attempt');
        }
      } catch (error) {
        setLoginTotpError(`${error}`);
      }

      if (successfulLogin) {
        setIsAuthenticated(true);
      }
      setLoggingIn(false);
    },
    [loginResult, loginTotpApi]
  );

  const logout = useCallback(async () => {
    setLoggingOut(true);
    setLogoutError(undefined);

    try {
      await logoutApi();
    } catch (error) {
      setLogoutError(`${error}`);
    }

    setIsAuthenticated(false);
    setLoginResult(undefined);
    setLoggingOut(false);

    mutate();
  }, [logoutApi, mutate]);

  const value = useMemo<AuthContextProps>(
    () => ({
      isAuthenticated,
      user: me,
      organizations,
      loadingUserDetails: loadingUserDetails || loadingOrganizations,
      showMfaModal,
      loggingIn,
      loggingOut,
      login,
      loginTotp,
      logout,
      loginResult,
      loginError,
      loginTotpError,
      logoutError,
      invalidate: mutate,
    }),
    [
      isAuthenticated,
      loadingOrganizations,
      loadingUserDetails,
      loggingIn,
      loggingOut,
      login,
      loginError,
      loginResult,
      loginTotp,
      loginTotpError,
      logout,
      logoutError,
      me,
      mutate,
      organizations,
      showMfaModal,
    ]
  );

  return (
    <SWRConfig value={authProviderSwrConfig}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </SWRConfig>
  );
};

export default AuthProvider;
